import { useState } from 'react';
import styles from './GebiedsanalyseModule.module.css';
import { Gebiedsprofiel } from '../../types';
import { Modal } from '../common/Modal/Modal';

interface GebiedsprofielSectionProps {
  data: Gebiedsprofiel | null;
  onRegenerate?: (customInstruction?: string) => void;
  loading?: boolean;
}

export default function GebiedsprofielSection({ data, onRegenerate, loading }: GebiedsprofielSectionProps) {
  const [showModal, setShowModal] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');

  if (!data) return null;

  const handleRegenerate = () => {
    if (onRegenerate) {
      onRegenerate(customInstruction.trim() || undefined);
    }
    setShowModal(false);
    setCustomInstruction('');
  };

  const handleQuickRegenerate = () => {
    if (onRegenerate) {
      onRegenerate();
    }
  };

  return (
    <div className={styles.section}>
      <h3>Gebiedsprofiel</h3>

      {data.narrative && (
        <div style={{
          background: 'white',
          borderRadius: 10,
          padding: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          marginBottom: 14
        }}>
          <p style={{
            fontFamily: "'Open Sans', sans-serif",
            fontSize: 13,
            color: '#323232',
            lineHeight: 1.7,
            margin: 0,
            whiteSpace: 'pre-line'
          }}>
            {data.narrative}
          </p>
        </div>
      )}

      {data.highlights?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <h4 style={{
            fontFamily: "'Open Sans', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            color: '#9DA6A8',
            textTransform: 'uppercase',
            margin: '0 0 8px',
            letterSpacing: '0.5px'
          }}>Kernpunten</h4>
          <div className={styles.adviezenList}>
            {data.highlights.map((highlight, i) => (
              <div key={i} className={styles.adviesCard} style={{ borderLeftColor: '#3b82f6' }}>
                <p>{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.aandachtspunten?.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <h4 style={{
            fontFamily: "'Open Sans', sans-serif",
            fontSize: 12,
            fontWeight: 600,
            color: '#9DA6A8',
            textTransform: 'uppercase',
            margin: '0 0 8px',
            letterSpacing: '0.5px'
          }}>Aandachtspunten participatie</h4>
          <div className={styles.adviezenList}>
            {data.aandachtspunten.map((punt, i) => (
              <div key={i} className={styles.adviesCard} style={{ borderLeftColor: '#f59e0b' }}>
                <p>{punt}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {onRegenerate && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            className={styles.refreshButton}
            onClick={handleQuickRegenerate}
            disabled={loading}
            style={{ flex: 1 }}
          >
            {loading ? 'Profiel genereren...' : 'Opnieuw genereren'}
          </button>
          <button
            className={styles.refreshButton}
            onClick={() => setShowModal(true)}
            disabled={loading}
            style={{
              flex: 1,
              background: '#E6007E',
              color: 'white',
              borderColor: '#E6007E'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}>
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            Met instructie
          </button>
        </div>
      )}

      {data.generatedAt && (
        <p style={{
          fontFamily: "'Open Sans', sans-serif",
          fontSize: 10,
          color: '#9DA6A8',
          margin: '8px 0 0',
          textAlign: 'center'
        }}>
          Gegenereerd op {new Date(data.generatedAt).toLocaleDateString('nl-NL')}
        </p>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setCustomInstruction(''); }}
        title="Profiel opnieuw genereren met instructie"
      >
        <div style={{ padding: '0.5rem 0' }}>
          <p style={{
            fontFamily: "'Open Sans', sans-serif",
            fontSize: 13,
            color: '#666',
            margin: '0 0 16px',
            lineHeight: 1.6
          }}>
            Geef een aanvullende instructie mee aan de AI om het gebiedsprofiel op een specifieke manier te laten genereren.
          </p>

          <textarea
            value={customInstruction}
            onChange={(e) => setCustomInstruction(e.target.value)}
            placeholder="Bijv. 'Focus meer op de jonge gezinnen in het gebied' of 'Besteed extra aandacht aan duurzaamheid en energietransitie' of 'Schrijf het profiel vanuit het perspectief van een woningbouwproject'"
            rows={5}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: '1px solid #ddd',
              fontFamily: "'Open Sans', sans-serif",
              fontSize: 13,
              lineHeight: 1.6,
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              onClick={() => { setShowModal(false); setCustomInstruction(''); }}
              style={{
                padding: '8px 20px',
                borderRadius: 8,
                border: '1px solid #ddd',
                background: 'white',
                cursor: 'pointer',
                fontFamily: "'Open Sans', sans-serif",
                fontSize: 13
              }}
            >
              Annuleren
            </button>
            <button
              onClick={handleRegenerate}
              disabled={!customInstruction.trim()}
              style={{
                padding: '8px 20px',
                borderRadius: 8,
                border: 'none',
                background: customInstruction.trim() ? '#E6007E' : '#ccc',
                color: 'white',
                cursor: customInstruction.trim() ? 'pointer' : 'not-allowed',
                fontFamily: "'Open Sans', sans-serif",
                fontSize: 13,
                fontWeight: 600
              }}
            >
              Genereren
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
